import axios from 'axios';
import { getUserTimezone } from '../constants/utils';

// Helper to read non-HttpOnly cookies (used for CSRF token)
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Create Axios instance with credentials enabled for HttpOnly cookie auth
const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 1000000,
  withCredentials: true,
});

// The refresh call gets its OWN bare client. The global `axios` instance carries
// api_helper's response interceptor, which replaces a failed response with a plain
// message string — that strips `.response`, so the 401/403 dead-session check in
// the interceptor below could never fire and an expired session zombied (endless
// failing requests) instead of cleanly logging out.
const refreshClient = axios.create({ withCredentials: true });

// Single source of truth for the refresh call, shared by the reactive interceptor
// below and the proactive keep-alive timer (useSessionKeepAlive). The trailing slash
// on the base URL is stripped so we never produce `…/arcmedix//auth/refresh` — the
// double slash falls outside the API's CORS mapping → preflight blocked → refresh
// always fails → forced logout.
export const refreshSession = () =>
  refreshClient.post(`${api.defaults.baseURL.replace(/\/+$/, '')}/auth/refresh`, {});

// Is the CSRF token cookie present AND JS-readable? The backend sets XSRF-TOKEN with
// HttpOnly=false (CookieCsrfTokenRepository.withHttpOnlyFalse), so the request
// interceptor can mirror it into the X-XSRF-TOKEN header.
export const xsrfTokenPresent = () => !!getCookie('XSRF-TOKEN');

// Ensure the CSRF cookie is committed + readable before the app issues any mutating
// request. After login there is a brief window where the cookie set by the login
// response hasn't landed yet; a mutating POST fired in that window goes token-less,
// Spring's CsrfFilter rejects it BEFORE any controller runs, and (because only an
// authenticationEntryPoint is wired) it surfaces as "Unauthorized access, please log
// in first." — looking like an auth failure. We prime by awaiting one authenticated
// GET: the backend writes XSRF-TOKEN on every response (CsrfCookieFilter forces the
// deferred token to materialize), so the cookie is guaranteed, not incidental. We
// gate on the COOKIE being readable, NOT on the GET status — if it somehow didn't
// land we hold rather than unblock on a green response.
export const ensureCsrfReady = async () => {
  if (xsrfTokenPresent()) return true;
  try { await api.get('auth/me'); } catch { /* priming failure is handled by the caller's own auth flow */ }
  for (let i = 0; i < 20 && !xsrfTokenPresent(); i += 1) {
    await new Promise((r) => setTimeout(r, 25)); // up to ~500ms for the cookie to commit
  }
  return xsrfTokenPresent();
};

// Track refresh state to prevent concurrent refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// Request Interceptor
api.interceptors.request.use(
  (config) => {
    // Add CSRF token from cookie (set by backend's CookieCsrfTokenRepository)
    const csrfToken = getCookie('XSRF-TOKEN');
    if (csrfToken) {
      config.headers['X-XSRF-TOKEN'] = csrfToken;
    }

    // Add timezone header
    config.headers['X-Timezone'] = getUserTimezone();

    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor with transparent token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // --- CSRF-miss safety net (defense-in-depth; the real fix is the post-login
    // ensureCsrfReady gate). A *mutating* request that 401'd WITHOUT having sent an
    // X-XSRF-TOKEN died in Spring's CsrfFilter BEFORE any controller ran — no side
    // effect occurred, so replaying it CANNOT double-create. (Do NOT widen this to
    // 401s whose request DID carry a token: those are post-controller failures where
    // a retry could duplicate.) Refreshing the JWT would not fix a missing CSRF token,
    // so we prime the cookie and replay once instead of rotating the session. If this
    // ever fires, the gate was bypassed (reload / SSO / a new mutation path) — log it
    // so we notice and close the gap at the source.
    const method = (originalRequest?.method || 'get').toUpperCase();
    const isMutating = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
    const sentXsrfToken = !!originalRequest?.headers?.['X-XSRF-TOKEN'];
    // /auth/** is CSRF-EXEMPT (ignoringRequestMatchers), so a token-less 401 there is
    // NOT a CsrfFilter rejection and could be post-controller — excluding it keeps the
    // "died in CsrfFilter before any handler → safe to replay" guarantee. That
    // guarantee holds for every entry point (password / SSO / reload) because the
    // backend has a single SecurityFilterChain: CSRF is checked before the
    // DispatcherServlet, independent of how auth was established.
    const csrfProtectedPath = !/(^|\/)auth\//.test(originalRequest?.url || '');
    if (status === 401 && isMutating && csrfProtectedPath && !sentXsrfToken && !originalRequest._csrfRetry) {
      originalRequest._csrfRetry = true;
      // eslint-disable-next-line no-console
      console.warn('[csrf] token-less mutating 401 — priming XSRF and retrying once (post-login gate bypassed?):', originalRequest?.url);
      await ensureCsrfReady();
      await new Promise((r) => setTimeout(r, 400)); // backoff: let the cookie settle (an instant replay re-fires into the same unsettled state)
      return api(originalRequest); // the request interceptor re-reads the now-present XSRF token
    }

    if (status === 401 && !originalRequest._retry) {
      // Mark BEFORE queuing so a queued request that 401s again on its retry
      // can't kick off a second refresh cycle (it will fall through instead).
      originalRequest._retry = true;

      if (isRefreshing) {
        // Queue this request until the refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      isRefreshing = true;

      try {
        await refreshSession();
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Only a definitive auth answer from the refresh endpoint means the
        // session is dead. A transient failure (network blip, backend restart
        // mid-deploy, 5xx) must not nuke a valid session — the request fails
        // and the next 401 simply attempts the refresh again.
        const refreshStatus = refreshError.response?.status;
        if (refreshStatus === 401 || refreshStatus === 403) {
          localStorage.removeItem('authUser');
          localStorage.removeItem('regionalApiUrl');
          localStorage.removeItem('region');
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response) {
      console.error("API Error:", error.response.data?.message || 'Unknown error');
    } else if (error.request) {
      console.error("No response from server");
    } else {
      console.error("Axios Error:", error.message);
    }

    return Promise.reject(error);
  }
);

export default api;
